/**
 * @fileoverview Minimal plug-and-play dropdown custom element
 *
 * @example
 * // Basic usage
 * <dropdown-element>
 *   <dropdown-trigger>
 *     <button>Select option</button>
 *   </dropdown-trigger>
 *   <dropdown-panel>
 *     <ul>
 *       <li>Option 1</li>
 *       <li>Option 2</li>
 *     </ul>
 *   </dropdown-panel>
 * </dropdown-element>
 *
 * @example
 * // Default open
 * <dropdown-element open>
 *   <dropdown-trigger>
 *     <button>Menu</button>
 *   </dropdown-trigger>
 *   <dropdown-panel>
 *     <p>Panel content</p>
 *   </dropdown-panel>
 * </dropdown-element>
 *
 * @example
 * // External trigger using dropdown-id
 * <dropdown-trigger dropdown-id="my-dropdown">
 *   <button>Toggle</button>
 * </dropdown-trigger>
 *
 * @example
 * // Programmatic control via window events
 * window.dispatchEvent(new Event('open-dropdown-my-dropdown'));
 * window.dispatchEvent(new Event('close-dropdown-my-dropdown'));
 * window.dispatchEvent(new Event('toggle-dropdown-my-dropdown'));
 */

const DROPDOWN_STYLES = `
  :host {
    display: inline-block;
    position: relative;
  }

  .dropdown-panel {
    position: absolute;
    top: 100%;
    left: 0;
    z-index: var(--dropdown-z-index, 100);
    min-width: 100%;
    visibility: hidden;
    opacity: 0;
    transition: opacity var(--dropdown-transition-duration, 0.2s) ease, visibility var(--dropdown-transition-duration, 0.2s) ease;
  }

  :host([open]) .dropdown-panel {
    visibility: visible;
    opacity: 1;
  }

  .dropdown-panel__inner {
    background: var(--dropdown-bg, #fff);
  }

  @media (prefers-reduced-motion: reduce) {
    .dropdown-panel {
      transition: none;
    }
  }
`;

class DropdownElement extends HTMLElement {
  /** @type {HTMLElement|null} */
  #panel = null;
  #boundToggle = null;
  #boundOpen = null;
  #boundClose = null;
  #boundClickOutside = null;
  #boundKeydown = null;

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this.shadowRoot.innerHTML = `
      <style>${DROPDOWN_STYLES}</style>
      <slot name="trigger"></slot>
      <div class="dropdown-panel">
        <div class="dropdown-panel__inner">
          <slot name="panel"></slot>
        </div>
      </div>
    `;

    // Bind methods for proper cleanup
    this.#boundToggle = () => this.toggle();
    this.#boundOpen = () => this.open();
    this.#boundClose = () => this.close();
    this.#boundClickOutside = (e) => {
      if (!this.contains(e.target)) this.close();
    };
    this.#boundKeydown = (e) => {
      if (e.key === "Escape") this.close();
    };
  }

  connectedCallback() {
    this.#panel = this.shadowRoot.querySelector(".dropdown-panel");

    // Listen on the shadow DOM slot
    const triggerSlot = this.shadowRoot.querySelector('slot[name="trigger"]');
    triggerSlot?.addEventListener("click", this.#boundToggle);

    // Set ARIA state once the trigger element gets slotted
    triggerSlot?.addEventListener("slotchange", () => {
      const trigger = this.querySelector("dropdown-trigger");
      if (trigger) {
        trigger.setAttribute(
          "aria-expanded",
          this.hasAttribute("open") ? "true" : "false",
        );
      }
    });

    // Window events for external control
    const id = this.id;
    if (id) {
      window.addEventListener(`open-dropdown-${id}`, this.#boundOpen);
      window.addEventListener(`close-dropdown-${id}`, this.#boundClose);
      window.addEventListener(`toggle-dropdown-${id}`, this.#boundToggle);
    }

    // If default open, attach outside listeners
    if (this.hasAttribute("open")) {
      document.addEventListener("click", this.#boundClickOutside);
      document.addEventListener("keydown", this.#boundKeydown);
    }
  }

  disconnectedCallback() {
    const id = this.id;
    if (id) {
      window.removeEventListener(`open-dropdown-${id}`, this.#boundOpen);
      window.removeEventListener(`close-dropdown-${id}`, this.#boundClose);
      window.removeEventListener(`toggle-dropdown-${id}`, this.#boundToggle);
    }
    document.removeEventListener("click", this.#boundClickOutside);
    document.removeEventListener("keydown", this.#boundKeydown);
  }

  /** Opens the dropdown */
  open() {
    this.setAttribute("open", "");
    this.querySelector("dropdown-trigger")?.setAttribute(
      "aria-expanded",
      "true",
    );
    document.addEventListener("click", this.#boundClickOutside);
    document.addEventListener("keydown", this.#boundKeydown);
    this.dispatchEvent(new CustomEvent("dropdown:open", { bubbles: true }));
  }

  /** Closes the dropdown */
  close() {
    this.removeAttribute("open");
    this.querySelector("dropdown-trigger")?.setAttribute(
      "aria-expanded",
      "false",
    );
    document.removeEventListener("click", this.#boundClickOutside);
    document.removeEventListener("keydown", this.#boundKeydown);
    this.dispatchEvent(new CustomEvent("dropdown:close", { bubbles: true }));
  }

  /** Toggles the dropdown */
  toggle() {
    if (this.hasAttribute("open")) {
      this.close();
    } else {
      this.open();
    }
  }

  /** @returns {boolean} Whether the dropdown is open */
  get isOpen() {
    return this.hasAttribute("open");
  }
}

class DropdownTrigger extends HTMLElement {
  connectedCallback() {
    this.setAttribute("slot", "trigger");
    this.style.cursor = "pointer";

    const hasFocusableChild = this.querySelector(
      'button, a, input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (!hasFocusableChild) {
      this.setAttribute("role", "button");
      this.setAttribute("tabindex", "0");

      this.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          this.click();
        }
      });
    }

    // External trigger support
    const dropdownId = this.getAttribute("dropdown-id");
    if (dropdownId) {
      this.addEventListener("click", () => {
        const dropdown = document.getElementById(dropdownId);
        if (dropdown?.isOpen) {
          window.dispatchEvent(new Event(`close-dropdown-${dropdownId}`));
        } else {
          window.dispatchEvent(new Event(`open-dropdown-${dropdownId}`));
        }
      });
    }
  }
}

class DropdownPanel extends HTMLElement {
  connectedCallback() {
    this.setAttribute("slot", "panel");
  }
}

customElements.define("dropdown-element", DropdownElement);
customElements.define("dropdown-trigger", DropdownTrigger);
customElements.define("dropdown-panel", DropdownPanel);
